import { FC, useMemo, useRef } from 'react';

import multicreationBidsBudgetsButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/auction/multicreation/multicreation_bids_budgets_button_click';
import {
    ActionList,
    Button,
    HSpacing,
    Link,
    TableCell,
    TableHeader,
    TableRow,
    Text,
    useBreakpoint,
    VSpacing,
} from '@hh.ru/magritte-ui';
import {
    EllipsisHorizontalCircleOutlinedSize16,
    PenOutlinedSize24,
    PlusCircleOutlinedSize16,
} from '@hh.ru/magritte-ui/icon';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';

import { AddVacanciesModal } from 'components/MultiCreationAuction/AddVacanciesModal/AddVacanciesModal';
import { ChangeAllBidsBudgetDrop } from 'components/MultiCreationAuction/BidsAndBudgetsTable/BidsAndBudgetsTableHeader/components/ChangeAllBidsBudgetDrop/ChangeAllBidsBudgetDrop';
import { ChangeAllDatesDrop } from 'components/MultiCreationAuction/BidsAndBudgetsTable/BidsAndBudgetsTableHeader/components/ChangeAllDatesDrop';
import { fetchVacancySearch } from 'components/MultiCreationAuction/VacanciesFiltersPanel/fetchVacancySearch';

import { BiddingStrategy } from 'store/models/auctionData';
import { AuctionCreateFieldKey, selectMultiCreationCampaignsCreating } from 'store/models/multiCreation';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/MultiCreationAuction/BidsAndBudgetsTable/BidsAndBudgetsTable.module.less';

const TrlKeys = {
    vacancy: 'MultiCreationAuction.BidsAndBudgetsTable.BidsAndBudgetsTableHeader.vacancy',
    bid: {
        columnHeader: {
            [BiddingStrategy.Auto]:
                'MultiCreationAuction.BidsAndBudgetsTable.BidsAndBudgetsTableHeader.bid.columnHeader.auto',
            [BiddingStrategy.Manual]:
                'MultiCreationAuction.BidsAndBudgetsTable.BidsAndBudgetsTableHeader.bid.columnHeader.manual',
        },
        actionListTitle: 'MultiCreationAuction.BidsAndBudgetsTable.BidsAndBudgetsTableHeader.bid.actionListTitle',
    },
    budget: {
        columnHeader: 'MultiCreationAuction.BidsAndBudgetsTable.BidsAndBudgetsTableHeader.budget.columnHeader',
        actionListTitle: 'MultiCreationAuction.BidsAndBudgetsTable.BidsAndBudgetsTableHeader.budget.actionListTitle',
    },
    startAt: {
        columnHeader: 'MultiCreationAuction.BidsAndBudgetsTable.BidsAndBudgetsTableHeader.startAt.columnHeader',
        actionListTitle: 'MultiCreationAuction.BidsAndBudgetsTable.BidsAndBudgetsTableHeader.startAt.actionListTitle',
    },
    endAt: {
        columnHeader: 'MultiCreationAuction.BidsAndBudgetsTable.BidsAndBudgetsTableHeader.endAt.columnHeader',
        actionListTitle: 'MultiCreationAuction.BidsAndBudgetsTable.BidsAndBudgetsTableHeader.endAt.actionListTitle',
    },
    add: 'MultiCreationAuction.BidsAndBudgetsTable.BidsAndBudgetsTableHeader.add',
    changeAll: 'MultiCreationAuction.BidsAndBudgetsTable.BidsAndBudgetsTableHeader.changeAll',
};

export const BidsAndBudgetsTableHeader: FC<{ paddingLeft: number; paddingRight: number }> = ({
    paddingLeft,
    paddingRight,
}) => {
    const vacanciesAuctionInfo = useSelector(({ multiCreation }) => multiCreation.vacanciesAuctionInfo);
    const biddingStrategy = useSelector(({ multiCreation }) => multiCreation.biddingStrategy);
    const isCampaignsCreating = useSelector(selectMultiCreationCampaignsCreating);
    const [isAddModalVisible, setIsAddModalVisibleOn, setIsAddModalVisibleOff] = useOnOffState(false);
    const [isChangeAllBidsDropVisible, setIsChangeAllBidsDropVisibleOn, setIsChangeAllBidsDropVisibleOff] =
        useOnOffState(false);
    const [isChangeAllBudgetsDropVisible, setIsChangeAllBudgetsDropVisibleOn, setIsChangeAllBudgetsDropVisibleOff] =
        useOnOffState(false);
    const [isChangeAllStartAtDropVisible, setIsChangeAllStartAtDropVisibleOn, setIsChangeAllStartAtDropVisibleOff] =
        useOnOffState(false);
    const [isChangeAllEndAtDropVisible, setIsChangeAllEndAtDropVisibleOn, setIsChangeAllEndAtDropVisibleOff] =
        useOnOffState(false);
    const [isActionListVisible, setIsActionListVisibleOn, setIsActionListVisibleOff] = useOnOffState(false);

    const isChangeAllBidsDisabled = useMemo(
        () => Boolean(vacanciesAuctionInfo && Object.values(vacanciesAuctionInfo).some((item) => item.bid.disabled)),
        [vacanciesAuctionInfo]
    );

    const { isMobile } = useBreakpoint();
    const trls = useTrls();
    const dispatch = useDispatch();
    const bidActivatorRef = useRef<HTMLButtonElement>(null);
    const budgetActivatorRef = useRef<HTMLButtonElement>(null);
    const startAtActivatorRef = useRef<HTMLButtonElement>(null);
    const endAtActivatorRef = useRef<HTMLButtonElement>(null);
    const actionListActivatorRef = useRef<HTMLButtonElement>(null);

    const handleAddVacancies = () => {
        multicreationBidsBudgetsButtonClick({ buttonName: 'multicreation_add' });
        void dispatch(fetchVacancySearch({}));
        setIsAddModalVisibleOn();
    };

    const handleAllBidsChange = () => {
        multicreationBidsBudgetsButtonClick({ buttonName: 'multicreation_change_all_bids' });
        setIsChangeAllBidsDropVisibleOn();
    };

    const handleAllBudgetsChange = () => {
        multicreationBidsBudgetsButtonClick({ buttonName: 'multicreation_change_all_budgets' });
        setIsChangeAllBudgetsDropVisibleOn();
    };

    const handleAllStartAtChange = () => {
        multicreationBidsBudgetsButtonClick({ buttonName: 'multicreation_change_all_start_at' });
        setIsChangeAllStartAtDropVisibleOn();
    };

    const handleAllEndAtChange = () => {
        multicreationBidsBudgetsButtonClick({ buttonName: 'multicreation_change_all_end_at' });
        setIsChangeAllEndAtDropVisibleOn();
    };

    return (
        <>
            {!isMobile ? (
                <TableHeader>
                    <TableRow align={'left'}>
                        <TableCell Element="th" m={12}>
                            <div className={styles.rowWrapper}>
                                <div className={styles.nameColumn}>
                                    <Text style="secondary" typography="label-2-regular">
                                        {trls(TrlKeys.vacancy)}
                                        <VSpacing default={4} />
                                        <Link
                                            data-qa="add-vacancies-button"
                                            Element="button"
                                            disabled={isCampaignsCreating}
                                            onClick={handleAddVacancies}
                                        >
                                            {trls(TrlKeys.add)}
                                        </Link>
                                    </Text>
                                </div>
                                <div className={styles.bidColumn}>
                                    <Text style="secondary" typography="label-2-regular">
                                        {trls(TrlKeys.bid.columnHeader[biddingStrategy])}
                                        <VSpacing default={4} />
                                        <Link
                                            data-qa="change-all-bids-button"
                                            disabled={isChangeAllBidsDisabled || isCampaignsCreating}
                                            Element="button"
                                            ref={bidActivatorRef}
                                            onClick={handleAllBidsChange}
                                        >
                                            {trls(TrlKeys.changeAll)}
                                        </Link>
                                    </Text>
                                </div>
                                <div className={styles.budgetColumn}>
                                    <Text style="secondary" typography="label-2-regular">
                                        {trls(TrlKeys.budget.columnHeader)}
                                        <VSpacing default={4} />
                                        <Link
                                            data-qa="change-all-budgets-button"
                                            disabled={isCampaignsCreating}
                                            Element="button"
                                            ref={budgetActivatorRef}
                                            onClick={handleAllBudgetsChange}
                                        >
                                            {trls(TrlKeys.changeAll)}
                                        </Link>
                                    </Text>
                                </div>
                                <div className={styles.startAtColumn}>
                                    <Text style="secondary" typography="label-2-regular">
                                        {trls(TrlKeys.startAt.columnHeader)}
                                        <VSpacing default={4} />
                                        <Link
                                            data-qa="change-all-start-at"
                                            disabled={isCampaignsCreating}
                                            Element="button"
                                            ref={startAtActivatorRef}
                                            onClick={handleAllStartAtChange}
                                        >
                                            {trls(TrlKeys.changeAll)}
                                        </Link>
                                    </Text>
                                </div>
                                <div className={styles.endAtColumn}>
                                    <Text style="secondary" typography="label-2-regular">
                                        {trls(TrlKeys.endAt.columnHeader)}
                                        <VSpacing default={4} />
                                        <Link
                                            data-qa="change-all-end-at"
                                            disabled={isCampaignsCreating}
                                            Element="button"
                                            ref={endAtActivatorRef}
                                            onClick={handleAllEndAtChange}
                                        >
                                            {trls(TrlKeys.changeAll)}
                                        </Link>
                                    </Text>
                                </div>
                            </div>
                        </TableCell>
                    </TableRow>
                </TableHeader>
            ) : (
                <div style={{ paddingLeft, paddingRight }}>
                    <Button
                        data-qa="add-vacancies-button-mobile"
                        mode="secondary"
                        size="small"
                        style="accent"
                        onClick={handleAddVacancies}
                        disabled={isCampaignsCreating}
                        icon={<PlusCircleOutlinedSize16 />}
                    >
                        {trls(TrlKeys.add)}
                    </Button>
                    <HSpacing default={8} />
                    <Button
                        data-qa="change-all-button-mobile"
                        ref={actionListActivatorRef}
                        mode="secondary"
                        size="small"
                        style="accent"
                        onClick={setIsActionListVisibleOn}
                        disabled={isCampaignsCreating}
                        icon={<EllipsisHorizontalCircleOutlinedSize16 />}
                    >
                        {trls(TrlKeys.changeAll)}
                    </Button>
                </div>
            )}
            <AddVacanciesModal isVisible={isAddModalVisible} closeModal={setIsAddModalVisibleOff} />
            <ChangeAllBidsBudgetDrop
                activatorRef={bidActivatorRef}
                isVisible={isChangeAllBidsDropVisible}
                closeDrop={setIsChangeAllBidsDropVisibleOff}
                fieldKey={AuctionCreateFieldKey.Bid}
            />
            <ChangeAllBidsBudgetDrop
                activatorRef={budgetActivatorRef}
                isVisible={isChangeAllBudgetsDropVisible}
                closeDrop={setIsChangeAllBudgetsDropVisibleOff}
                fieldKey={AuctionCreateFieldKey.Budget}
            />
            <ChangeAllDatesDrop
                activatorRef={startAtActivatorRef}
                isVisible={isChangeAllStartAtDropVisible}
                closeDrop={setIsChangeAllStartAtDropVisibleOff}
                fieldKey={AuctionCreateFieldKey.StartAt}
            />
            <ChangeAllDatesDrop
                activatorRef={endAtActivatorRef}
                isVisible={isChangeAllEndAtDropVisible}
                closeDrop={setIsChangeAllEndAtDropVisibleOff}
                fieldKey={AuctionCreateFieldKey.EndAt}
            />
            <ActionList
                visible={isActionListVisible}
                onClose={setIsActionListVisibleOff}
                dropProps={{
                    placement: 'bottom',
                    activatorRef: actionListActivatorRef,
                }}
            >
                <ul role="menu" aria-orientation="vertical" className={styles.actionList}>
                    {['bid', 'budget', 'startAt', 'endAt'].map((item) => (
                        <li key={item} role="menuitem" className={styles.actionListItem}>
                            <Button
                                data-qa={`action-list-button-${item}`}
                                stretched
                                size="large"
                                style="neutral"
                                mode="tertiary"
                                icon={<PenOutlinedSize24 />}
                                onClick={() => {
                                    if (item === 'bid') {
                                        setIsActionListVisibleOff();
                                        handleAllBidsChange();
                                    }
                                    if (item === 'budget') {
                                        setIsActionListVisibleOff();
                                        handleAllBudgetsChange();
                                    }
                                    if (item === 'startAt') {
                                        setIsActionListVisibleOff();
                                        handleAllStartAtChange();
                                    }
                                    if (item === 'endAt') {
                                        setIsActionListVisibleOff();
                                        handleAllEndAtChange();
                                    }
                                }}
                            >
                                {trls(TrlKeys[item as AuctionCreateFieldKey].actionListTitle)}
                            </Button>
                        </li>
                    ))}
                </ul>
            </ActionList>
        </>
    );
};
